import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, List, ListItem, ListItemText, Divider, Button, Grid,
  AppBar, Toolbar, IconButton, Avatar, Menu, MenuItem } from '@mui/material';
import { useNavigate, useLocation, useParams, Outlet } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import axios from 'axios';
import { BACKEND_DOMAIN } from '../../../../common/env';

const AdminDashboardWithSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auctionId } = useParams();
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState('auctions'); // 'auctions' or 'profile'
  const [anchorEl, setAnchorEl] = useState(null);
  const token = localStorage.getItem('token');

  // Determine active section based on URL
  useEffect(() => {
    if (location.pathname === '/admin-dashboard/profile') {
      setActiveSection('profile');
    } else {
      setActiveSection('auctions');
    }
  }, [location]);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${BACKEND_DOMAIN}/my-details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    };

    fetchUserData();
  }, [token]);

  const handleSignOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    navigate('/');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="redesigned-admin-dashboard">
      {/* Navbar */}
      <AppBar position="fixed" className="admin-navbar" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Logo on the left */}
          <Typography 
            variant="h6" 
            component="div" 
            className="navbar-logo"
            sx={{ cursor: 'pointer', fontWeight: 'bold' }}
            onClick={() => navigate('/')}
          >
            🏆 MyLiveAuction
          </Typography>
          
          {/* "My Dashboard" in the center */}
          <Typography 
            variant="h6" 
            component="div" 
            className="navbar-title"
            sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
          >
            My Dashboard
          </Typography>
          
          {/* User icon on the right */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
          >
            {user && user.username ? (
              <Avatar className="user-avatar" sx={{ width: 32, height: 32, fontSize: 16 }}>
                {user.username.charAt(0).toUpperCase()}
              </Avatar>
            ) : (
              <AccountCircleIcon />
            )}
          </IconButton>
          
          {/* User menu */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => { handleMenuClose(); navigate('/admin-dashboard/profile'); }} className="user-menu-item">
              My Profile
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); handleSignOut(); }} className="user-menu-item">
              Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', minHeight: '100vh', pt: '64px' }}>
        {/* Left Side Panel */}
        <Card className="side-panel" elevation={3}>
          <CardContent>
            <Divider sx={{ my: 2 }} />
            <List component="nav">
              <ListItem 
                button 
                selected={activeSection === 'auctions'}
                onClick={() => navigate('/admin-dashboard')}
                className={`nav-item ${activeSection === 'auctions' ? 'active' : ''}`}
              >
                <ListItemText primary="My Auctions" />
              </ListItem>
              <ListItem 
                button 
                selected={activeSection === 'profile'}
                onClick={() => navigate('/admin-dashboard/profile')}
                className={`nav-item ${activeSection === 'profile' ? 'active' : ''}`}
              >
                <ListItemText primary="My Profile" />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Main Content Area */}
        <Box className="main-content" sx={{ flex: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </div>
  );
};

export default AdminDashboardWithSidebar;